/**
 * 1.8.0: a sequence's rights in the editor (the top-bar pill and the Rights side tab), from the document and the
 * sources' rights (/sequences/:id/sources items carry `rights` while rights management is on). Pure.
 */
import {
  allClips, combineRights, sourcesOf, type CombinedRights, type GenealogySourceInfo, type RightsSummary, type SequenceDocument,
} from '@prism/contracts';
import type { SequenceSource } from '../../api/hooks';

export interface SequenceRightsRow {
  assetId: string;
  title: string;
  /** The tracks it is on, in the order first met. */
  tracks: string[];
  /** Each use's programme in and out (ticks), in record order. */
  uses: Array<{ track: string; recordInTicks: number; recordOutTicks: number }>;
  /** The programme time it fills, all uses together (ticks). */
  usedTicks: number;
  rights: RightsSummary | null;
}

/** sourcesOf's source info from the sources list: each version's asset and kind (an image is a still). */
export function sourceInfoOf(sources: readonly SequenceSource[]): GenealogySourceInfo {
  return {
    versions: Object.fromEntries(sources.map((s) => [s.versionId, { assetId: s.assetId, kind: s.mediaKind === 'image' ? 'still' as const : s.mediaKind === 'audio' ? 'audio' as const : 'video' as const }])),
  };
}

/** One row per source asset the document uses (graphics with an asset included), in the order first used. */
export function sequenceRightsRows(doc: SequenceDocument, sources: readonly SequenceSource[]): SequenceRightsRow[] {
  const byVersion = new Map(sources.map((s) => [s.versionId, s]));
  const byAsset = new Map(sources.map((s) => [s.assetId, s]));
  const rows = new Map<string, SequenceRightsRow>();
  for (const u of sourcesOf(doc, sourceInfoOf(sources))) {
    if (!u.assetId) continue;
    const src = (u.versionId ? byVersion.get(u.versionId) : undefined) ?? byAsset.get(u.assetId);
    let r = rows.get(u.assetId);
    if (!r) {
      r = { assetId: u.assetId, title: src?.title ?? 'Unknown asset', tracks: [], uses: [], usedTicks: 0, rights: src?.rights ?? null };
      rows.set(u.assetId, r);
    }
    if (!r.tracks.includes(u.track)) r.tracks.push(u.track);
    r.uses.push({ track: u.track, recordInTicks: u.recordInTicks, recordOutTicks: u.recordOutTicks });
    r.usedTicks += Math.max(0, u.recordOutTicks - u.recordInTicks);
  }
  return [...rows.values()];
}

/** The rows' rights together (combineRights); null when no source carries rights (rights management is off). */
export function combinedOf(rows: readonly SequenceRightsRow[]): CombinedRights | null {
  const list = rows.flatMap((r) => (r.rights ? [r.rights] : []));
  return list.length ? combineRights(list) : null;
}

/** The ids of the clips whose source is withdrawn (the editor's toast when one is added). */
export function withdrawnClipIds(doc: SequenceDocument, sources: ReadonlyMap<string, SequenceSource>): Set<string> {
  return new Set(allClips(doc).filter((c) => sources.get(c.clip.versionId)?.rights?.state === 'withdrawn').map((c) => c.clip.id));
}
